import React from 'react';
import ObjectPath from 'object-path';
import Dropdown from '@hobs/components/Dropdown';
import DateInput from '@hobs/components/DateInput';
import InputLabel from '@hobs/components/InputLabel';
import Switch from '@hobs/components/Switch';
import NumericInput from '@hobs/components/NumericInput';
import BluePanel from '@hobs/components/BluePanel';
import FieldHistoryMessage from '@hobs/components/FieldHistoryMessage';
import { withTranslation } from 'react-i18next';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import { dateToInitialMonth } from '@hobs/utils/dateToInitialMonth';
import Quantity from '../Quantity';
import Money from '../Money';
import CalculateByDays from '../CalculateByDays';

import './DeliveryInformation.scss';
import ActivityDropdown from '../ActivityDropdown/activityDropdown';

const DeliveryInformation = ({
  results,
  setFieldValue,
  units,
  activities,
  uoms,
  t,
  disabled,
  showPlaceholderOption,
  showTotalEstimates,
  checked,
  editedFields = [],
  values = {},
  activityInForm,
  exerciseCurrency,
  schema = {
    unitId: 'unitId',
    dateFrom: 'dateFrom',
    quantity: 'quantity',
    unitOfMeasureId: 'unitOfMeasureId',
    dateTo: 'dateTo',
    isCalculatedByDays: 'isCalculatedByDays',
    activityId: 'activityId'
  }
}) => {
  var getUnitOptions = units => {
    if (units && units.length) {
      return []
        .concat(
          [
            {
              title: t('Select Unit'),
              value: ''
            }
          ],
          units
        )
        .map(unit => {
          return (
            <option
              disabled={unit && unit.title && unit.title.includes('(approved)')}
              key={ShortId.generate()}
              value={unit.value}
            >
              {unit.title}
            </option>
          );
        });
    }
    return (
      <option value="">
        {t(
          'There are no SN units added for this Exercise. Please add them in SOR requisites'
        )}
      </option>
    );
  };

  return (
    <div className="delivery-information">
      <div className="delivery-information--row">
        <div className="delivery-information--form">
          <div className="delivery-information--row">
            <Field
              setFieldValue={setFieldValue}
              label={t('To Unit')}
              name={schema.unitId}
              component={Dropdown}
              disabled={disabled}
              indicationMessage={FieldHistoryMessage('UnitName', editedFields)}
            >
              {getUnitOptions(units)}
            </Field>
            <div className="date">
              <Field
                setFieldValue={setFieldValue}
                name={schema.dateFrom}
                disabled={disabled}
                component={DateInput}
                indicationMessage={FieldHistoryMessage(
                  'DateFrom',
                  editedFields
                )}
              >
                {t('From date')}
              </Field>
            </div>
          </div>
          <div className="delivery-information--row">
            <div className="delivery-information--quantity">
              <Field
                setFieldValue={setFieldValue}
                name={schema.quantity}
                placeholder="Quantity"
                component={NumericInput}
                disabled={disabled}
                min={0}
                indicationMessage={FieldHistoryMessage(
                  'RequestedQuantity',
                  editedFields
                )}
              >
                {t('Req. Quantity')}
              </Field>
            </div>
            <div className="delivery-information--UoM">
              <Field
                setFieldValue={setFieldValue}
                label={t('UoM')}
                name={schema.unitOfMeasureId}
                placeholder="UoM"
                component={Dropdown}
                disabled={uoms.length === 0 || disabled}
                indicationMessage={FieldHistoryMessage(
                  'UnitOfMeasureName',
                  editedFields
                )}
              >
                {(uoms.length === 0 || showPlaceholderOption) && (
                  <option value="">{t('UoM')}</option>
                )}
                {uoms.map(uom => (
                  <option key={ShortId.generate()} value={uom.value}>
                    {uom.title}
                  </option>
                ))}
              </Field>
            </div>
            <div className="delivery-information--row">
              <div className="date">
                <Field
                  setFieldValue={setFieldValue}
                  name={schema.dateTo}
                  initialMonth={dateToInitialMonth(
                    values.dateFrom,
                    values.dateTo
                  )}
                  disabled={disabled}
                  component={DateInput}
                  indicationMessage={FieldHistoryMessage(
                    'DateTo',
                    editedFields
                  )}
                >
                  {t('To date')}
                </Field>
              </div>
            </div>
          </div>
          <div className="delivery-information--row">
            <InputLabel>
              {t(
                'Please verify and make sure ordered quantity and period is correct.'
              )}
            </InputLabel>
            <div className="delivery-information--column">
              <CalculateByDays
                name={schema.isCalculatedByDays}
                component={Switch}
                disabled={disabled}
                checked={checked}
                setFieldValue={setFieldValue}
                indicationMessage={FieldHistoryMessage(
                  'IsCalculatedByDays',
                  editedFields
                )}
                differenceInDays={results ? results.differenceInDays : null}
              />
            </div>
          </div>
          {activities && activities.length !== 0 && (
            <ActivityDropdown
              disabled={disabled}
              indicationMessage={FieldHistoryMessage(
                'ActivityName',
                editedFields
              )}
              sr={values}
              activities={activities}
              value={values.activityId}
              activityInForm={activityInForm}
              setFieldValue={setFieldValue}
              fieldName={schema.activityId}
            />
          )}
        </div>

        <BluePanel>
          <div className="delivery-information--cost-panel">
            <div className="item">
              {t('Total quantity')}
              <InputLabel
                indicationMessage={FieldHistoryMessage(
                  'CalculatedTotalQuantity',
                  editedFields
                )}
              />
              <strong>
                {results ? <Quantity amount={results.quantity} /> : '-'}
              </strong>
            </div>

            {showTotalEstimates && (
              <div className="item">
                {t('Est. total cost')}
                <span> ({exerciseCurrency})</span>
                <InputLabel
                  indicationMessage={FieldHistoryMessage(
                    'EstimatedTotalCostFrom',
                    editedFields
                  )}
                />

                {results ? (
                  <strong>
                    <Money amount={ObjectPath.get(results, 'rateFrom', '')} />
                    {(results.rateFrom && results.rateTo) ||
                    (!results.rateFrom && !results.rateTo)
                      ? ' - '
                      : ''}
                    <Money amount={ObjectPath.get(results, 'rateTo', '')} />
                  </strong>
                ) : (
                  '-'
                )}
              </div>
            )}
          </div>
        </BluePanel>
      </div>
    </div>
  );
};

DeliveryInformation.propTypes = {
  units: PropTypes.array,
  disabled: PropTypes.bool,
  uoms: PropTypes.array,
  activities: PropTypes.array,
  results: PropTypes.object,
  showPlaceholderOption: PropTypes.bool,
  showTotalEstimates: PropTypes.bool,
  checked: PropTypes.bool,
  setFieldValue: PropTypes.func,
  t: PropTypes.func,
  editedFields: PropTypes.array,
  values: PropTypes.object,
  activityInForm: PropTypes.bool,
  exerciseCurrency: PropTypes.string
};

export default withTranslation()(DeliveryInformation);
